import React from "react";

// plain text 안의 URL을 클릭 가능한 링크로 변환한다.
// XSS 방지를 위해 dangerouslySetInnerHTML 없이 텍스트를 분할해 <a>로 렌더한다.
// 텍스트 노드를 그대로 보존하므로 white-space: pre-line 등 줄바꿈 스타일과 함께 쓸 수 있다.
// 꺾쇠(<, >)는 URL 구분자로 보고 매칭에서 제외한다(예: <https://example.com> 형태).
export const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>]+)/gi;

const CLOSERS = { ")": "(", "]": "[", "}": "{" };
// 항상 URL 끝에서 떼어내는 문장 부호
const SIMPLE_TRAILING = ".,!?'\"";

// URL 끝에 붙은 문장 부호를 떼어낸다.
// 닫는 괄호는 URL 안에 짝이 되는 여는 괄호가 있으면(균형) 보존한다.
// 예: ".../Multi_(sport)" 의 마지막 ')' 는 URL의 일부로 유지한다.
// TuiViewer(에디터 HTML 본문 자동 링크)도 같은 규칙을 쓰도록 export 한다.
export const splitTrailingPunctuation = (raw) => {
    let url = raw;
    let trailing = "";
    while (url.length) {
        const ch = url[url.length - 1];
        if (SIMPLE_TRAILING.includes(ch)) {
            trailing = ch + trailing;
            url = url.slice(0, -1);
            continue;
        }
        const opener = CLOSERS[ch];
        if (opener) {
            const opens = url.split(opener).length - 1;
            const closes = url.split(ch).length - 1;
            // 닫는 괄호가 여는 괄호보다 많을 때만(불균형) 문장 부호로 보고 제거
            if (closes > opens) {
                trailing = ch + trailing;
                url = url.slice(0, -1);
                continue;
            }
        }
        break;
    }
    return { url, trailing };
};

export const renderWithLinks = (text, linkClassName = "auto-link") => {
    if (!text) return null;
    return text.split(URL_REGEX).map((part, i) => {
        if (!part) return null;
        if (!/^(?:https?:\/\/|www\.)/i.test(part)) {
            return <React.Fragment key={i}>{part}</React.Fragment>;
        }
        const { url, trailing } = splitTrailingPunctuation(part);
        const href = /^www\./i.test(url) ? `https://${url}` : url;
        return (
            <React.Fragment key={i}>
                <a
                    className={linkClassName}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {url}
                </a>
                {trailing}
            </React.Fragment>
        );
    });
};

// 멘션 토큰(<@id|Name>)을 분리하기 위한 split 정규식 (캡처 그룹으로 토큰 보존)
const MENTION_SPLIT_REGEX = /(<@[^|>]+\|[^>]+>)/g;
const MENTION_ONE_REGEX = /^<@([^|>]+)\|([^>]+)>$/;

// 커뮤니티 프로필을 새 탭으로 연다(커뮤니티 관례).
const openMentionProfile = (userId) => {
    window.open(
        `/community/user/${encodeURIComponent(userId)}`,
        "_blank",
        "noopener"
    );
};

// URL + @멘션 토큰을 함께 렌더한다. dangerouslySetInnerHTML 없이 노드로만 구성.
// 멘션이 아닌 구간은 기존 renderWithLinks 로 URL 링크화한다.
export const renderWithMentions = (text, linkClassName = "auto-link") => {
    if (!text) return null;
    return text.split(MENTION_SPLIT_REGEX).map((part, i) => {
        if (!part) return null;
        const m = MENTION_ONE_REGEX.exec(part);
        if (m) {
            const [, userId, display] = m;
            return (
                <a
                    key={`m${i}`}
                    className="mention"
                    href={`/community/user/${encodeURIComponent(userId)}`}
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.preventDefault();
                        openMentionProfile(userId);
                    }}
                >
                    @{display}
                </a>
            );
        }
        return (
            <React.Fragment key={`t${i}`}>
                {renderWithLinks(part, linkClassName)}
            </React.Fragment>
        );
    });
};

export default renderWithLinks;
